import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getRaffles, getPacks, getWinners, getSettings } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useLang } from '../lib/lang';
import useCountUp from '../hooks/useCountUp';
import RaffleCard from '../components/RaffleCard';
import CountdownTimer from '../components/CountdownTimer';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import InlineEditable from '../components/InlineEditable';
import WinnerPhotoCarousel from '../components/WinnerPhotoCarousel';
import WinnerCard from '../components/WinnerCard';
import HeroShowcase from '../components/HeroShowcase';
import { ShieldCheck, Zap, Award, CreditCard, Trophy, ArrowRight, Sparkles, Star, Gift, Clock, Users, User, Coins, Ticket, Quote, ChevronLeft, ChevronRight } from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 14 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.16, 1, 0.3, 1] } },
};
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

export default function HomePage() {
  const { user } = useAuth();
  const { t } = useLang();
  const [raffles, setRaffles] = useState([]);
  const [packs, setPacks] = useState([]);
  const [winners, setWinners] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const winnersScrollRef = useRef(null);

  const scrollWinners = (dir) => {
    const el = winnersScrollRef.current;
    if (!el) return;
    const amount = Math.max(320, el.clientWidth * 0.85);
    el.scrollBy({ left: dir * amount, behavior: 'smooth' });
  };
  const totalPrizeValue = winners.reduce((sum, w) => sum + (Number(w.product_value) || 0), 0);

  useEffect(() => {
    async function load() {
      try {
        const [r, p, w, s] = await Promise.all([getRaffles(), getPacks(), getWinners(), getSettings()]);
        setRaffles(r.data.raffles || []);
        setPacks(p.data.packs || []);
        setWinners(w.data.winners || []);
        setSettings(s.data.settings || {});
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    }
    load();
  }, []);

  const ff = settings?.feature_flags || {};
  const wo = settings?.welcome_offer || null;
  const showWelcomeBanner = !!wo?.enabled && Number(wo.amount || 0) > 0 && (!user || (Number(user.total_entries || 0) === 0 && !user.welcome_offer_used));
  // Hero raffle: pick the one explicitly featured by admin, or fallback to first
  const heroRaffle = raffles.find((r) => r.featured === true) || raffles[0];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-rolex-green">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-gold border-t-transparent rounded-full animate-spin mx-auto mb-5" />
          <p className="text-cream/70 text-sm uppercase tracking-[0.3em] font-semibold">Loading WINLUXOR…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-rolex-green">
      {showWelcomeBanner && (
        <Link to={user ? '/#raffles' : '/register'} className="block bg-gold-gradient text-rolex-green text-center text-sm font-bold py-2.5 px-4 hover:brightness-105 transition-all" data-testid="welcome-offer-banner">
          <Gift className="w-4 h-4 inline mr-1.5 -mt-0.5" />
          {(t('welcomeBannerText') || '🎁 £{amount} off your first entry — claim it now').replace('{amount}', Number(wo.amount).toFixed(0))}
        </Link>
      )}
      {/* HERO — Immersive stacked showcase (all live competitions) */}
      {raffles.length > 0 && (
        <HeroShowcase raffles={raffles} settings={settings} />
      )}

      {/* TRUST STRIP - Cream */}
      {ff.trust_section !== false && (
        <section className="py-10 sm:py-12 bg-section-alt border-y border-[hsl(var(--gold)/0.18)]" data-testid="home-trust-strip">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              {[
                { icon: ShieldCheck, labelKey: 'securePayments', descKey: 'sslEncrypted', label: t('securePayments'), desc: t('sslEncrypted') },
                { icon: Award, labelKey: 'ukCompliant', descKey: 'fullyRegulated', label: t('ukCompliant'), desc: t('fullyRegulated') },
                { icon: Users, labelKey: 'realWinners', descKey: 'verifiedDraws', label: t('realWinners'), desc: t('verifiedDraws') },
                { icon: Zap, labelKey: 'instantCredits', descKey: 'pointsAddedInstantly', label: t('instantCredits'), desc: t('pointsAddedInstantly') },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 sm:gap-4 p-4 sm:p-5 rounded-2xl bg-ivory border border-[hsl(var(--gold)/0.18)] shadow-sm hover:shadow-md hover:border-[hsl(var(--gold)/0.45)] transition-all duration-200">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gold-gradient flex items-center justify-center shrink-0 shadow-md">
                    <item.icon className="w-6 h-6 sm:w-7 sm:h-7 text-rolex-green" strokeWidth={2} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="font-display font-bold text-sm sm:text-base text-rolex-green leading-tight"><InlineEditable tKey={item.labelKey}>{item.label}</InlineEditable></h4>
                    <p className="text-xs text-rolex-green/60 mt-0.5 sm:mt-1 leading-snug"><InlineEditable tKey={item.descKey}>{item.desc}</InlineEditable></p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ACTIVE RAFFLES - Dark Green */}
      {ff.raffles !== false && raffles.length > 0 && (
        <section id="raffles" className="py-20 sm:py-24 bg-rolex-green relative" data-testid="home-active-raffles">
          <div className="absolute inset-0 hero-spotlight pointer-events-none opacity-50" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
              <motion.div variants={fadeUp} className="flex items-end justify-between mb-12 gap-4 flex-wrap">
                <div>
                  <p className="gold-line mb-3"><Sparkles className="w-3 h-3" /> <InlineEditable tKey="liveCompetitions">{t('liveCompetitions') || 'Live Competitions'}</InlineEditable></p>
                  <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-cream"><InlineEditable tKey="activeDraws">{t('activeDraws')}</InlineEditable></h2>
                  <p className="text-cream/60 mt-3 text-base font-light"><InlineEditable tKey="dontMiss" multiline>{t('dontMiss')}</InlineEditable></p>
                </div>
                <Badge variant="outline" className="border-[hsl(var(--gold)/0.4)] text-gold bg-[hsl(var(--gold)/0.08)] gap-1.5 text-xs px-3 py-1.5 tracking-wider uppercase">
                  <Clock className="w-3 h-3" />
                  {raffles.length} <InlineEditable tKey="drawsLive">{t('drawsLive')}</InlineEditable>
                </Badge>
              </motion.div>
              <motion.div variants={stagger} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
                {raffles.map((raffle) => (
                  <motion.div key={raffle._id} variants={fadeUp}>
                    <RaffleCard raffle={raffle} showTickets={ff.show_tickets !== false} pointRate={settings?.point_rate || 1} />
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </section>
      )}

      {/* HOW IT WORKS - Cream */}
      {ff.how_it_works !== false && (
        <section id="how-it-works" className="py-20 sm:py-24 bg-section-alt" data-testid="home-how-it-works">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-14">
              <motion.div variants={fadeUp}>
                <p className="gold-line mb-4 mx-auto justify-center"><InlineEditable tKey="process">{t('process') || 'Process'}</InlineEditable></p>
                <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-rolex-green"><InlineEditable tKey="howItWorksTitle">{t('howItWorksTitle')}</InlineEditable></h2>
                <p className="text-rolex-green/60 mt-4 text-base max-w-2xl mx-auto font-light"><InlineEditable tKey="howItWorksSubtitle" multiline>{t('howItWorksSubtitle')}</InlineEditable></p>
              </motion.div>
            </motion.div>
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className={`grid grid-cols-1 sm:grid-cols-2 ${ff.packs === false ? 'lg:grid-cols-4' : 'lg:grid-cols-5'} gap-4 sm:gap-5 mb-12`}>
              {[
                { num: '01', icon: User,        titleKey: 'step1Title', descKey: 'step1Desc', title: t('step1Title'), desc: t('step1Desc'), gradient: 'from-[#0E4936] to-[#08291F]' },
                { num: '02', icon: CreditCard,  titleKey: 'step2Title', descKey: 'step2Desc', title: t('step2Title'), desc: t('step2Desc'), gradient: 'from-[#D4AF37] to-[#A07828]', isGold: true, requiresPacks: true },
                { num: '03', icon: Trophy,      titleKey: 'step3Title', descKey: 'step3Desc', title: t('step3Title'), desc: t('step3Desc'), gradient: 'from-[#0E4936] to-[#08291F]' },
                { num: '04', icon: ShieldCheck, titleKey: 'step4Title', descKey: 'step4Desc', title: t('step4Title'), desc: t('step4Desc'), gradient: 'from-[#0E4936] to-[#08291F]' },
                { num: '05', icon: Gift,        titleKey: 'step5Title', descKey: 'step5Desc', title: t('step5Title'), desc: t('step5Desc'), gradient: 'from-[#D4AF37] to-[#A07828]', isGold: true },
              ]
                // Filter out the "buy points" step when the packs system is disabled
                .filter((step) => !(step.requiresPacks && ff.packs === false))
                // Renumber the remaining steps so the displayed numbers stay sequential
                .map((step, i, arr) => ({ ...step, num: String(i + 1).padStart(2, '0') }))
                .map((step, i) => (
                <motion.div key={i} variants={fadeUp}>
                  <div className={`group relative overflow-hidden rounded-2xl border-2 ${step.isGold ? 'border-gold' : 'border-[hsl(var(--gold)/0.30)]'} h-full bg-ivory shadow-md hover:shadow-2xl hover:-translate-y-1.5 transition-all duration-300`}>
                    {/* Top decorative gradient strip */}
                    <div className={`h-2 bg-gradient-to-r ${step.gradient}`} />
                    <div className="p-5 sm:p-6">
                      {/* Step number watermark */}
                      <div className="flex items-start justify-between mb-3">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-md bg-gradient-to-br ${step.gradient}`}>
                          <step.icon className={`w-6 h-6 ${step.isGold ? 'text-rolex-green' : 'text-gold'}`} strokeWidth={2} />
                        </div>
                        <span className="font-mono text-3xl font-extrabold text-gold/30 group-hover:text-gold/55 transition-colors">{step.num}</span>
                      </div>
                      <h3 className="font-display text-base sm:text-lg font-extrabold mb-2 text-rolex-green leading-tight"><InlineEditable tKey={step.titleKey}>{step.title}</InlineEditable></h3>
                      <p className="text-xs sm:text-sm text-rolex-green/65 leading-relaxed"><InlineEditable tKey={step.descKey} multiline>{step.desc}</InlineEditable></p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* CTA Double : Acheter des points + ticket direct.
                Entire block is hidden when packs are disabled (the "Concours"
                link in the header is sufficient to reach the raffles). */}
            {ff.packs !== false && (
            <motion.div initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mx-auto">
              <a href="#packs" className="group block h-full">
                <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0E4936] to-[#08291F] border-2 border-[hsl(var(--gold)/0.40)] p-6 hover:border-gold transition-all gold-glow h-full" style={{ color: '#F5EFE0' }}>
                  <div className="absolute inset-0 noise-overlay opacity-30 pointer-events-none" />
                  <div className="relative flex items-start gap-3 h-full">
                    <div className="w-12 h-12 rounded-xl bg-gold-gradient flex items-center justify-center shrink-0 shadow-md">
                      <Coins className="w-6 h-6 text-rolex-green" strokeWidth={2} />
                    </div>
                    <div className="flex-1 min-w-0 flex flex-col">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <p className="text-[10px] uppercase tracking-[0.22em] font-extrabold" style={{ color: '#D4AF37' }}><InlineEditable tKey="option1">{t('option1') || 'Option 1'}</InlineEditable></p>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gold-gradient text-[9px] font-extrabold uppercase tracking-[0.1em] shadow-md whitespace-nowrap" style={{ color: '#0B3D2E' }}>
                          🎁 +<InlineEditable tKey="bonus">{t('bonus') || 'BONUS'}</InlineEditable>
                        </span>
                      </div>
                      <h4 className="font-display text-lg font-extrabold mb-1" style={{ color: '#F5EFE0' }}><InlineEditable tKey="buyPointsOption">{t('buyPointsOption') || 'Buy points'}</InlineEditable></h4>
                      <p className="text-xs leading-relaxed flex-1" style={{ color: 'rgba(245, 239, 224, 0.78)' }}><InlineEditable tKey="buyPointsOptionDesc" multiline>{t('buyPointsOptionDesc') || 'Top up your wallet and earn up to +30% bonus points on bigger packs.'}</InlineEditable></p>
                      <p className="mt-2 text-xs font-bold flex items-center gap-1" style={{ color: '#D4AF37' }}><InlineEditable tKey="seePacks">{t('seePacks') || 'See packs'}</InlineEditable> <ArrowRight className="w-3 h-3" /></p>
                    </div>
                  </div>
                </div>
              </a>
              <a href="#raffles" className="group block h-full">
                <div className="relative overflow-hidden rounded-2xl bg-gold-gradient border-2 border-rolex-green p-6 hover:scale-[1.02] transition-transform shadow-md h-full">
                  <div className="absolute inset-0 noise-overlay opacity-25 pointer-events-none" />
                  <div className="relative flex items-start gap-3 h-full">
                    <div className="w-12 h-12 rounded-xl bg-rolex-green flex items-center justify-center shrink-0 shadow-md">
                      <Ticket className="w-6 h-6 text-gold" strokeWidth={2} />
                    </div>
                    <div className="flex-1 min-w-0 flex flex-col">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <p className="text-[10px] uppercase tracking-[0.22em] font-extrabold" style={{ color: '#0B3D2E' }}><InlineEditable tKey="option2">{t('option2') || 'Option 2'}</InlineEditable></p>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rolex-green text-[9px] font-extrabold uppercase tracking-[0.1em] shadow-md whitespace-nowrap" style={{ color: '#E5C97A' }}>
                          ⚡ <InlineEditable tKey="fast">{t('fast') || 'FAST'}</InlineEditable>
                        </span>
                      </div>
                      <h4 className="font-display text-lg font-extrabold mb-1" style={{ color: '#0B3D2E' }}><InlineEditable tKey="buyTicketDirectOption">{t('buyTicketDirectOption') || 'Buy a ticket directly'}</InlineEditable></h4>
                      <p className="text-xs leading-relaxed font-medium flex-1" style={{ color: 'rgba(11, 61, 46, 0.85)' }}><InlineEditable tKey="buyTicketDirectDesc" multiline>{t('buyTicketDirectDesc') || 'Skip the points — pick a draw and grab tickets in one click. Pay & enter.'}</InlineEditable></p>
                      <p className="mt-2 text-xs font-extrabold flex items-center gap-1" style={{ color: '#0B3D2E' }}><InlineEditable tKey="seeDraws">{t('seeDraws') || 'See draws'}</InlineEditable> <ArrowRight className="w-3 h-3" /></p>
                    </div>
                  </div>
                </div>
              </a>
            </motion.div>
            )}
          </div>
        </section>
      )}

      {/* URGENCY - Dark Green Stripe (only shown if at least one raffle ends within 24h) */}
      {ff.urgency !== false && raffles.some(r => {
        if (!r.ends_at) return false;
        const end = new Date(r.ends_at);
        const diffHours = (end - new Date()) / (1000 * 60 * 60);
        return diffHours > 0 && diffHours <= 24;
      }) && (
        <section className="py-16 bg-rolex-green relative overflow-hidden" data-testid="home-urgency">
          <div className="absolute inset-0 hero-spotlight opacity-70 pointer-events-none" />
          <div className="max-w-4xl mx-auto px-4 text-center relative">
            <div className="divider-gold-thick mb-10 max-w-xs mx-auto" />
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-3 text-cream"><InlineEditable tKey="urgencyTitle">{t('urgencyTitle')}</InlineEditable></h2>
            <p className="text-cream/65 mb-7 text-base font-light"><InlineEditable tKey="urgencyDesc" multiline>{t('urgencyDesc')}</InlineEditable></p>
            <div className="flex flex-wrap items-center justify-center gap-8 mb-8 text-sm">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-full bg-[hsl(var(--gold)/0.12)] border border-[hsl(var(--gold)/0.3)] flex items-center justify-center">
                  <Users className="w-4 h-4 text-gold" />
                </div>
                <div className="text-left">
                  <span className="font-mono text-xl font-bold text-cream block">{raffles.reduce((s, r) => s + (r.sold_tickets || 0), 0).toLocaleString()}</span>
                  <span className="text-cream/50 text-xs uppercase tracking-wider"><InlineEditable tKey="totalParticipants">{t('totalParticipants')}</InlineEditable></span>
                </div>
              </div>
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-full bg-[hsl(var(--gold)/0.12)] border border-[hsl(var(--gold)/0.3)] flex items-center justify-center">
                  <Clock className="w-4 h-4 text-gold" />
                </div>
                <div className="text-left">
                  <span className="font-mono text-xl font-bold text-cream block">{raffles.length}</span>
                  <span className="text-cream/50 text-xs uppercase tracking-wider"><InlineEditable tKey="drawsLive">{t('drawsLive')}</InlineEditable></span>
                </div>
              </div>
            </div>
            <Link to={user ? '/#raffles' : '/register'}>
              <Button size="lg" className="btn-gold btn-press gap-2 rounded-xl uppercase tracking-wider font-bold px-9 h-14">
                <InlineEditable tKey="enterNow">{t('enterNow')}</InlineEditable>
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </section>
      )}

      {/* Random Draw Certificate — fairness/transparency, admin-configurable */}
      {settings?.random_draw_certificate_enabled !== false && settings?.random_draw_certificate_url && (
        <section className="py-20 sm:py-24 bg-rolex-green-deep relative overflow-hidden" data-testid="home-random-cert">
          <div className="absolute inset-0 noise-overlay opacity-50 pointer-events-none" />
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-gold to-transparent" />
          <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-gold to-transparent" />
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
              <motion.div variants={fadeUp} className="text-center mb-12">
                <p className="gold-line mb-4 mx-auto justify-center"><Award className="w-3 h-3" /> <InlineEditable tKey="randomDrawCertLabel">{t('randomDrawCertLabel') || 'Transparence absolue'}</InlineEditable></p>
                <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-cream">
                  <InlineEditable tKey="randomDrawCertTitle">{t('randomDrawCertTitle') || 'Tirages 100% aléatoires & certifiés'}</InlineEditable>
                </h2>
                <p className="text-cream/70 mt-4 text-base sm:text-lg max-w-2xl mx-auto font-light leading-relaxed">
                  <InlineEditable tKey="randomDrawCertDesc" multiline>{t('randomDrawCertDesc') || 'Chaque tirage est effectué via un système aléatoire certifié et indépendant — le résultat est public et vérifiable. Vous avez la garantie d\'une équité totale.'}</InlineEditable>
                </p>
              </motion.div>

              {/* The luxury framed certificate */}
              <motion.div variants={fadeUp} className="flex justify-center">
                <div className="relative inline-block max-w-2xl w-full group">
                  {/* Outer ambient halo */}
                  <div className="absolute -inset-12 bg-gold/8 rounded-[3rem] blur-3xl pointer-events-none" />
                  <div className="absolute -inset-1 bg-gradient-to-br from-gold/30 via-transparent to-gold/30 rounded-[2rem] blur-md pointer-events-none" />

                  {/* The frame itself — single elegant gold border with brushed-metal feel */}
                  <div
                    className="relative rounded-[1.75rem] p-[18px] sm:p-[24px]"
                    style={{
                      background: `
                        linear-gradient(135deg,
                          hsl(43 35% 25%) 0%,
                          hsl(43 70% 55%) 12%,
                          hsl(43 85% 75%) 25%,
                          hsl(43 60% 45%) 38%,
                          hsl(43 90% 80%) 50%,
                          hsl(43 60% 45%) 62%,
                          hsl(43 85% 75%) 75%,
                          hsl(43 70% 55%) 88%,
                          hsl(43 35% 25%) 100%
                        )
                      `,
                      boxShadow: `
                        0 30px 80px -20px rgba(0,0,0,0.6),
                        0 12px 30px -6px hsl(43 67% 53% / 0.45),
                        inset 0 0 0 1px hsl(43 80% 75% / 0.5),
                        inset 0 2px 4px hsl(43 100% 95% / 0.5),
                        inset 0 -2px 4px rgba(0,0,0,0.4)
                      `,
                    }}
                  >
                    {/* Engraved bevel ring */}
                    <div
                      className="absolute inset-[10px] sm:inset-[14px] rounded-[1.35rem] pointer-events-none"
                      style={{
                        boxShadow: `
                          inset 0 2px 6px rgba(0,0,0,0.6),
                          inset 0 -1px 2px hsl(43 100% 95% / 0.35),
                          0 0 0 1px hsl(43 40% 30% / 0.6)
                        `,
                      }}
                    />

                    {/* Certificate inside a subtle ivory mat with deep shadow */}
                    <div
                      className="relative rounded-[1.15rem] overflow-hidden"
                      style={{
                        boxShadow: `
                          inset 0 0 0 1px hsl(43 80% 75% / 0.4),
                          0 4px 14px rgba(0,0,0,0.35),
                          0 1px 2px rgba(0,0,0,0.2)
                        `,
                      }}
                    >
                      <img
                        src={settings.random_draw_certificate_url}
                        alt="Certificate of Random Draw"
                        className="block w-full h-auto"
                        style={{ background: '#fefcf6' }}
                      />
                    </div>

                    {/* Ornate SVG corner decorations (scrollwork) */}
                    {[
                      { pos: 'top-0 left-0', rotate: 0 },
                      { pos: 'top-0 right-0', rotate: 90 },
                      { pos: 'bottom-0 right-0', rotate: 180 },
                      { pos: 'bottom-0 left-0', rotate: 270 },
                    ].map((c, i) => (
                      <svg
                        key={i}
                        viewBox="0 0 60 60"
                        className={`absolute ${c.pos} w-12 h-12 sm:w-14 sm:h-14 pointer-events-none`}
                        style={{
                          transform: `rotate(${c.rotate}deg)`,
                          transformOrigin: 'center',
                          filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.5))',
                        }}
                      >
                        <defs>
                          <linearGradient id={`corner-grad-${i}`} x1="0" y1="0" x2="1" y2="1">
                            <stop offset="0%" stopColor="hsl(43 100% 90%)" />
                            <stop offset="40%" stopColor="hsl(43 80% 65%)" />
                            <stop offset="80%" stopColor="hsl(43 55% 40%)" />
                            <stop offset="100%" stopColor="hsl(43 35% 25%)" />
                          </linearGradient>
                        </defs>
                        {/* Curved scrollwork ornament */}
                        <path
                          d="M 6 6 L 24 6 Q 18 6 14 10 Q 6 18 6 24 Z M 14 22 Q 16 16 22 14 L 18 18 Q 16 20 14 22 Z"
                          fill={`url(#corner-grad-${i})`}
                          stroke="hsl(43 30% 18%)"
                          strokeWidth="0.5"
                        />
                        {/* Small accent dot */}
                        <circle cx="10" cy="10" r="2" fill="hsl(43 100% 92%)" opacity="0.9" />
                      </svg>
                    ))}
                  </div>

                  {/* Caption underneath — premium look */}
                  <div className="mt-7 text-center">
                    <div className="inline-flex items-center gap-3 px-5 py-2 rounded-full" style={{
                      background: 'linear-gradient(135deg, hsl(158 55% 8%) 0%, hsl(158 55% 13%) 100%)',
                      boxShadow: '0 6px 16px rgba(0,0,0,0.4), 0 0 0 1px hsl(43 67% 45% / 0.45), inset 0 1px 2px hsl(43 80% 65% / 0.15)',
                    }}>
                      <ShieldCheck className="w-4 h-4" style={{ color: 'hsl(43 80% 65%)' }} />
                      <span className="text-[11px] font-semibold tracking-[0.18em] uppercase" style={{ color: 'hsl(43 50% 85%)' }}>
                        <InlineEditable tKey="randomDrawCertCaption">{t('randomDrawCertCaption') || 'Certifié par randomdraws.com — vérifiable en ligne'}</InlineEditable>
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>

            {/* Verification CTA — only when admin configured the URL */}
            {settings?.random_draw_verification_url && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.25 }}
                className="mt-8 sm:mt-10 max-w-2xl mx-auto"
                data-testid="random-draw-verification-block"
              >
                <div className="rounded-2xl bg-white/95 backdrop-blur border-2 border-gold/45 shadow-[0_15px_45px_rgba(20,22,26,0.10)] p-6 sm:p-7 text-center">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <span className="text-2xl">🔍</span>
                    <h3 className="font-display text-xl sm:text-2xl font-bold" style={{ color: '#0E4936' }}>
                      {t('verifyTicketTitle') || 'Vérifiez votre numéro de ticket'}
                    </h3>
                  </div>
                  <p className="text-sm leading-relaxed mb-5 max-w-lg mx-auto" style={{ color: '#0E4936' }}>
                    {t('verifyTicketIntro') || "Tous nos tirages sont effectués via un service de tirage aléatoire indépendant pour garantir une équité totale. Cliquez ci-dessous, saisissez votre numéro de ticket et vérifiez qu'il fait partie du tirage officiel."}
                  </p>
                  <a
                    href={settings.random_draw_verification_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 rounded-xl bg-gradient-to-r from-gold-light via-gold to-gold-dark text-rolex-green font-extrabold text-sm sm:text-base shadow-lg hover:opacity-90 transition-all uppercase tracking-wider"
                    data-testid="random-draw-verification-link"
                  >
                    🎯 {t('verifyTicketCta') || 'Vérifier mon ticket'}
                    <span aria-hidden="true">→</span>
                  </a>
                </div>
              </motion.div>
            )}

          </div>
        </section>
      )}

      {/* WINNERS — Cinematic dark gallery */}
      {ff.winners !== false && winners.length > 0 && (
        <section id="winners" className="relative py-24 sm:py-28 bg-rolex-green overflow-hidden" data-testid="home-winners-carousel">
          <div className="absolute inset-0 hero-spotlight opacity-60 pointer-events-none" />
          <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[60rem] h-[60rem] rounded-full bg-[radial-gradient(circle,hsl(var(--gold)/0.10),transparent_60%)] pointer-events-none" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
              {/* Header */}
              <motion.div variants={fadeUp} className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
                <div className="max-w-2xl">
                  <p className="gold-line mb-4"><Star className="w-3 h-3" /> <InlineEditable tKey="hallOfWinners">{t('hallOfWinners') || 'Hall of Winners'}</InlineEditable></p>
                  <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-cream leading-[1.05]"><InlineEditable tKey="recentWinners">{t('recentWinners')}</InlineEditable></h2>
                  <p className="text-cream/60 mt-4 text-base font-light"><InlineEditable tKey="realPeopleRealPrizes" multiline>{t('realPeopleRealPrizes')}</InlineEditable></p>
                </div>
                <div className="flex items-center gap-4 shrink-0">
                  <div className="hidden lg:flex gap-2">
                    {winners.length > 1 && (<>
                    <button onClick={() => scrollWinners(-1)} className="w-11 h-11 rounded-full border border-gold/30 text-cream/80 flex items-center justify-center hover:bg-gold hover:text-rolex-green transition-colors" data-testid="winners-prev" aria-label="Previous"><ChevronLeft className="w-5 h-5" /></button>
                    <button onClick={() => scrollWinners(1)} className="w-11 h-11 rounded-full border border-gold/30 text-cream/80 flex items-center justify-center hover:bg-gold hover:text-rolex-green transition-colors" data-testid="winners-next" aria-label="Next"><ChevronRight className="w-5 h-5" /></button>
                    </>)}
                  </div>
                </div>
              </motion.div>

              {/* Cards row */}
              <motion.div variants={stagger}>
                <div ref={winnersScrollRef} className={`flex gap-6 pb-6 ${winners.length === 1 ? 'justify-center' : 'overflow-x-auto snap-x snap-mandatory scroll-smooth no-scrollbar -mx-4 px-4 sm:mx-0 sm:px-0'}`}>
                  {winners.map((w, i) => (
                    <motion.div key={i} variants={fadeUp} data-testid="winner-card" className={winners.length === 1 ? 'w-full max-w-sm' : 'snap-start shrink-0 w-[82vw] sm:w-[58vw] md:w-[44vw] lg:w-[31%] xl:w-[28%]'}>
                      <WinnerCard w={w} />
                    </motion.div>
                  ))}
                </div>
                {winners.length > 1 && (
                  <p className="text-center text-xs text-cream/40 mt-2 lg:hidden">← <InlineEditable tKey="swipeToSee">{t('swipeToSee') || 'Glissez pour voir plus'}</InlineEditable> →</p>
                )}
              </motion.div>
            </motion.div>
          </div>
        </section>
      )}

      {/* POINT PACKS - Dark Green */}
      {ff.packs !== false && packs.length > 0 && (
        <section id="packs" className="py-20 sm:py-24 bg-rolex-green relative" data-testid="home-point-packs">
          <div className="absolute inset-0 hero-spotlight opacity-50 pointer-events-none" />
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
              <motion.div variants={fadeUp} className="text-center mb-12">
                <p className="gold-line mb-4 mx-auto justify-center"><InlineEditable tKey="topUp">{t('topUp') || 'Top Up'}</InlineEditable></p>
                <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-cream"><InlineEditable tKey="pointPacks">{t('pointPacks')}</InlineEditable></h2>
                <p className="text-cream/60 mt-3 text-base font-light"><InlineEditable tKey="buyPointsDesc" multiline>{t('buyPointsDesc')}</InlineEditable></p>
              </motion.div>
              <motion.div variants={stagger} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {packs.map((pack) => (
                  <motion.div key={pack._id} variants={fadeUp}>
                    <div className={`relative rounded-2xl p-6 text-center card-glass card-premium-hover ${pack.popular ? 'gold-glow-strong' : ''}`}>
                      {pack.popular && <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gold-gradient text-rolex-green border-none text-[10px] font-bold tracking-widest uppercase z-10 px-3 py-1"><InlineEditable tKey="mostPopular">{t('mostPopular')}</InlineEditable></Badge>}
                      <h3 className="font-display text-lg font-bold mb-2 text-cream">{pack.name}</h3>
                      <p className="text-[10px] text-cream/55 uppercase tracking-wider mb-1"><InlineEditable tKey="creditReceived">{t('creditReceived') || 'Crédit reçu'}</InlineEditable></p>
                      <div className="flex items-baseline justify-center gap-1 my-3">
                        <span className="text-cream/70 text-2xl font-bold">£</span>
                        <span className="font-mono text-5xl font-bold text-gold-gradient">
                          {Math.round((pack.points || 0) * (1 + (pack.bonus_pct || 0) / 100))}
                        </span>
                      </div>
                      {pack.bonus_pct > 0 && <Badge variant="outline" className="mb-4 border-green-400/50 text-green-300 bg-green-900/30 text-[10px] uppercase tracking-wider">+{pack.bonus_pct}% <InlineEditable tKey="bonus">{t('bonus')}</InlineEditable></Badge>}
                      <p className="text-[10px] text-cream/55 uppercase tracking-wider mb-1"><InlineEditable tKey="youPay">{t('youPay') || 'Vous payez'}</InlineEditable></p>
                      <p className="font-mono text-2xl font-bold text-cream mb-5">£{pack.price_gbp}</p>
                      <Link to={user ? '/wallet' : '/register'}>
                        <Button className={`w-full btn-press rounded-xl tracking-wider uppercase text-xs font-bold ${pack.popular ? 'btn-gold' : 'border border-[hsl(var(--gold)/0.4)] bg-transparent text-cream hover:bg-[hsl(var(--gold)/0.10)]'}`} data-testid="point-pack-buy-button">
                          <InlineEditable tKey="buyPoints">{t('buyPoints')}</InlineEditable>
                        </Button>
                      </Link>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </section>
      )}

      {/* FAQ - Cream */}
      {ff.faq !== false && (
        <section id="faq" className="py-20 sm:py-24 bg-section-alt" data-testid="home-faq">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
              <motion.div variants={fadeUp} className="text-center mb-12">
                <p className="gold-line mb-4 mx-auto justify-center">FAQ</p>
                <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-rolex-green"><InlineEditable tKey="faqTitle">{t('faqTitle')}</InlineEditable></h2>
              </motion.div>
              <motion.div variants={fadeUp}>
                <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
                  {['faq1', 'faq2', 'faq3', 'faq4', 'faq5', 'faq6'].map((key, i) => (
                    <AccordionItem key={i} value={`faq-${i}`} className="bg-ivory border border-[hsl(var(--gold)/0.2)] rounded-xl px-6 hover:border-[hsl(var(--gold)/0.45)] transition-colors" data-testid="faq-item">
                      <AccordionTrigger className="text-sm font-semibold hover:text-gold-dark py-5 text-rolex-green"><InlineEditable tKey={`${key}q`}>{t(`${key}q`)}</InlineEditable></AccordionTrigger>
                      <AccordionContent className="text-sm text-rolex-green/70 pb-5 leading-relaxed font-light"><InlineEditable tKey={`${key}a`} multiline>{t(`${key}a`)}</InlineEditable></AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </motion.div>
            </motion.div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}

// ─── Hero featured raffle — premium effects helpers ─────────────
/**
 * Slot-machine style count-up for the featured raffle value. Animates 0 → target
 * on mount/raffle-change, then flashes the gold text once when it reaches the
 * final value. Adds to the "premium reveal" feeling of the hero.
 */
function HeroValueCounter({ target, dependencyKey, size = 'lg' }) {
  const value = useCountUp(target, { duration: 1800, delayMs: 400, dependencyKey });
  const isDone = value === target;
  const sizeClass = size === 'sm' ? 'text-sm' : 'text-lg';
  return (
    <span
      key={`done-${dependencyKey}-${isDone ? 1 : 0}`}
      className={`text-gold font-extrabold ${sizeClass} tabular-nums drop-shadow-[0_1px_4px_rgba(0,0,0,0.95)] ${isDone ? 'count-up-flash' : ''}`}
    >
      £{(value || 0).toLocaleString()}
    </span>
  );
}
